import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";

const AuthContext = createContext(null);

// Auth tokens live in secure httpOnly cookies set by the backend (see set_auth_cookies).
// We never persist the JWT in JS-accessible storage to avoid XSS token theft.
export function AuthProvider({ children }) {
  // null = checking; false = not auth; object = user
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      // Not authenticated — expected on first load
      if (e?.response?.status !== 401) {
        // eslint-disable-next-line no-console
        console.error("Auth check failed:", e);
      }
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Skip if returning from OAuth
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    return data.user;
  };

  const register = async (email, password, name, referralCode) => {
    const payload = { email, password, name };
    if (referralCode) payload.referral_code = referralCode;
    const { data } = await api.post("/auth/register", payload);
    setUser(data.user);
    return data.user;
  };

  const loginWithGoogleSession = async (session_id) => {
    const { data } = await api.post("/auth/google/session", { session_id });
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("Logout API failed; clearing local session anyway.", e);
    }
    setUser(false);
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, login, register, loginWithGoogleSession, logout, formatApiError, refresh: checkAuth }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
